import useSWR from "swr";
import fetcher from "@adora/shared/utils/fetch";
import { RelativeUrl, EcsRelativeUrl } from "@adora/shared/utils/environment";
import patch from "@adora/shared/utils/patch";
import post from "@adora/shared/utils/post";

export interface Experiment {
  id: number;
  brand_id: number;
  creator_id: number;
  name: string;
  description: string;
  product?: string;
  reference_image_path: string;
  reference_experiment_id?: number;
  caption_job_batch_id?: number;
  training_job_batch_id?: number;
  inference_job_bulk_id?: number;
  product_lora_id?: number;
  blob_rate?: number;
  wrong_shoe_rate?: number;
  incoherent_rate?: number;
  auto_accepted_rate?: number;
  manual_blob_rate?: number;
  manual_wrong_shoe_rate?: number;
  manual_incoherent_rate?: number;
  manual_bad_laces_rate?: number;
  manual_bad_fine_details_rate?: number;
  manual_accepted_rate?: number;
  overfitting_manual?: string;
  result_notes?: string;
  filtered_hit_rate?: number;
}

function getExperimentsUrl(brandId: string | number | undefined) {
  const baseUrl = `${RelativeUrl}/experiments`;

  return brandId ? `${baseUrl}?brand_id=${encodeURIComponent(String(brandId))}` : null;
}

export default function useExperiments(brandId: string | number | undefined) {
  const { data, error, isLoading, mutate } = useSWR(getExperimentsUrl(brandId), (url) => fetcher<Experiment[]>(url), {
    revalidateOnFocus: true,
    revalidateOnReconnect: true
  });

  const markExperimentEvaled = async (experimentId: number) => {
    const url = `${RelativeUrl}/experiments/${experimentId}/mark_evaled`;
    const result = await patch(url, { brand_id: Number(brandId) });

    await mutate();
    return result;
  };

  const rerunExperimentEval = async (experimentId: number) => {
    const url = `${EcsRelativeUrl}/experiments/${experimentId}/launch_auto_eval`;
    const result = await post(url, { brand_id: Number(brandId) });

    await mutate();
    return result;
  };

  const retryExperiment = async (experimentId: number) => {
    const url = `${EcsRelativeUrl}/experiments/${experimentId}/retry`;
    const result = await post(url, { brand_id: Number(brandId) });

    await mutate();
    return result;
  };

  const updateExperiment = async (
    experimentId: number,
    data: { overfitting_manual?: string; result_notes?: string }
  ) => {
    const url = `${RelativeUrl}/experiments/${experimentId}`;
    const result = await patch(url, { brand_id: Number(brandId) }, { ...data });

    await mutate();
    return result;
  };

  return {
    experiments: data || [],
    isLoading,
    error,
    mutate,
    markExperimentEvaled,
    rerunExperimentEval,
    retryExperiment,
    updateExperiment
  };
}
